import React, { useState, useEffect } from 'react';
import settingsService, { MotifRefoulement } from '@/services/settingsService';

interface MotifRefoulementSelectorProps {
  selectedMotif?: string;
  onMotifChange: (motifId: string) => void;
  className?: string;
  placeholder?: string;
  showDescription?: boolean;
  groupByCategory?: boolean;
}

const MotifRefoulementSelector: React.FC<MotifRefoulementSelectorProps> = ({
  selectedMotif,
  onMotifChange,
  className = '',
  placeholder = 'Sélectionner un motif de refoulement',
  showDescription = false,
  groupByCategory = true
}) => {
  const [motifs, setMotifs] = useState<MotifRefoulement[]>([]);
  const [motifsByCategory, setMotifsByCategory] = useState<Record<string, MotifRefoulement[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Charger les motifs au montage du composant
  useEffect(() => {
    loadMotifs();
  }, []);

  const loadMotifs = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (groupByCategory) {
        const motifsByCat = await settingsService.getMotifsRefoulementByCategorie();
        setMotifsByCategory(motifsByCat);
        
        // Aplatir tous les motifs pour la recherche
        const allMotifs = Object.values(motifsByCat).flat();
        setMotifs(allMotifs);
      } else {
        const allMotifs = await settingsService.getMotifsRefoulement();
        setMotifs(allMotifs);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des motifs:', err);
      setError('Erreur lors du chargement des motifs de refoulement');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    if (query.trim()) {
      try {
        const results = await settingsService.searchMotifsRefoulement(query);
        setMotifs(results);
      } catch (err) {
        console.error('Erreur lors de la recherche:', err);
      }
    } else {
      // Recharger tous les motifs
      loadMotifs();
    }
  };

  const getCriticiteColor = (criticite: string) => {
    switch (criticite) {
      case 'Critique':
        return 'text-red-600 bg-red-100';
      case 'Haute':
        return 'text-orange-600 bg-orange-100';
      case 'Moyenne':
        return 'text-yellow-600 bg-yellow-100';
      case 'Faible':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getSelectedMotif = () => {
    return motifs.find(motif => motif.id === selectedMotif);
  };

  const filteredMotifs = searchQuery.trim() 
    ? motifs.filter(motif => 
        motif.libelle.toLowerCase().includes(searchQuery.toLowerCase()) ||
        motif.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        motif.categorie.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : motifs;

  if (loading) {
    return (
      <div className={`relative ${className}`}>
        <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500 mr-2"></div>
            <span className="text-gray-500">Chargement des motifs...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`relative ${className}`}>
        <div className="w-full px-3 py-2 border border-red-300 rounded-lg bg-red-50">
          <div className="flex items-center">
            <i className="fas fa-exclamation-triangle text-red-500 mr-2"></i>
            <span className="text-red-600">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {/* Bouton de sélection */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 text-left border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
      >
        {selectedMotif ? (
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">{getSelectedMotif()?.libelle}</div>
              {showDescription && (
                <div className="text-sm text-gray-500 truncate">
                  {getSelectedMotif()?.description}
                </div>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <span className={`px-2 py-1 text-xs rounded-full ${getCriticiteColor(getSelectedMotif()?.criticite || '')}`}>
                {getSelectedMotif()?.criticite}
              </span>
              <i className={`fas fa-chevron-down transform transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-gray-500">{placeholder}</span>
            <i className={`fas fa-chevron-down transform transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
          </div>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-96 overflow-hidden">
          {/* Barre de recherche */}
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher un motif..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
              <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
            </div>
          </div>

          {/* Liste des motifs */}
          <div className="max-h-80 overflow-y-auto">
            {groupByCategory && !searchQuery.trim() ? (
              // Affichage groupé par catégorie
              Object.entries(motifsByCategory).map(([categorie, motifsCategorie]) => (
                <div key={categorie} className="border-b border-gray-100 last:border-b-0">
                  <div className="px-3 py-2 bg-gray-50 text-sm font-medium text-gray-700 border-b border-gray-200">
                    {categorie} ({motifsCategorie.length})
                  </div>
                  {motifsCategorie.map((motif) => (
                    <button
                      key={motif.id}
                      onClick={() => {
                        onMotifChange(motif.id);
                        setIsOpen(false);
                      }}
                      className={`w-full px-3 py-2 text-left hover:bg-gray-50 ${
                        selectedMotif === motif.id ? 'bg-orange-50 border-r-4 border-orange-500' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{motif.libelle}</div>
                          {showDescription && (
                            <div className="text-sm text-gray-500 mt-1 line-clamp-2">
                              {motif.description}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-2 ml-3">
                          <span className="text-xs text-gray-500">{motif.code}</span>
                          <span className={`px-2 py-1 text-xs rounded-full ${getCriticiteColor(motif.criticite)}`}>
                            {motif.criticite}
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ))
            ) : (
              // Affichage simple (recherche ou non groupé)
              filteredMotifs.map((motif) => (
                <button
                  key={motif.id}
                  onClick={() => {
                    onMotifChange(motif.id);
                    setIsOpen(false);
                  }}
                  className={`w-full px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                    selectedMotif === motif.id ? 'bg-orange-50 border-r-4 border-orange-500' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{motif.libelle}</div>
                      <div className="text-sm text-gray-500 mt-1">
                        {motif.categorie} • {motif.description}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-3">
                      <span className="text-xs text-gray-500">{motif.code}</span>
                      <span className={`px-2 py-1 text-xs rounded-full ${getCriticiteColor(motif.criticite)}`}>
                        {motif.criticite}
                      </span>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Message si aucun résultat */}
          {filteredMotifs.length === 0 && (
            <div className="px-3 py-4 text-center text-gray-500">
              <i className="fas fa-search text-2xl mb-2"></i>
              <p>Aucun motif trouvé</p>
            </div>
          )}
        </div>
      )}

      {/* Overlay pour fermer le dropdown */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default MotifRefoulementSelector;
